import { useEffect, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import classNames from 'classnames/bind';
import GeneralIcon, { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import PropTypes from 'prop-types';
import styles from './Tooltip.module.css';

const cx = classNames.bind(styles);

/**
 * Stated here, mirroring the `propTypes` and `defaultProps` blocks below,
 * because neither reaches the JSX checker: `...rest` gives the props an index
 * signature, and React's prop transforms bail out on that — so every
 * destructured prop was inferred as required and `<Tooltip className="…">` was
 * an error for not also passing `arrowStyle`, `contentStyle` and `iconProps`.
 * The index signature stands for the props `...rest` forwards to `Popup`.
 *
 * @typedef {{
 *   arrowStyle?: object,
 *   children?: import('react').ReactNode,
 *   className?: string,
 *   contentStyle?: object,
 *   iconProps?: object,
 *   showIcon?: boolean,
 * } & Record<string, any>} TooltipProps
 */

/** @param {TooltipProps} props */
const Tooltip = ({
  arrowStyle,
  children,
  className,
  contentStyle,
  iconProps,
  onClose,
  onOpen,
  showIcon = true,
  ...rest
}) => {
  const popupRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  /**
   * The tooltip is positioned against the viewport, so it stays put while the
   * page — or the modal it sits in — scrolls away underneath it. Close it
   * instead. The listener is in the capture phase because `scroll` does not
   * bubble: without it, scrolling an inner container never reaches `document`.
   */
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScroll = () => popupRef.current?.close();

    document.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });

    return () =>
      document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [isOpen]);

  return (
    <Popup
      ref={popupRef}
      onOpen={(event) => {
        setIsOpen(true);
        onOpen?.(event);
      }}
      onClose={(event) => {
        setIsOpen(false);
        onClose?.(event);
      }}
      contentStyle={{
        backgroundColor: '#0f2532',
        color: 'white',
        border: 'none',
        borderRadius: 16,
        padding: '20px',
        width: 300,
        whiteSpace: 'normal',
        ...contentStyle,
      }}
      arrowStyle={{
        color: '#0f2532',
        stroke: 'transparent',
        filter: 'none',
        ...arrowStyle,
      }}
      trigger={
        <div className={cx('tooltip', className)}>
          {showIcon && (
            <GeneralIcon icon={GENERAL_ICONS.TOOLTIP} {...iconProps} />
          )}
        </div>
      }
      position="top center"
      arrow={false}
      on={['hover', 'click']}
      keepTooltipInside
      repositionOnResize
      {...rest}
    >
      <span className="text-p1 leading-p1 lg:text-body lg:leading-body">
        {children}
      </span>
    </Popup>
  );
};

Tooltip.propTypes = {
  arrowStyle: PropTypes.object,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contentStyle: PropTypes.object,
  iconProps: PropTypes.object,
  showIcon: PropTypes.bool,
};

Tooltip.defaultProps = {
  className: '',
  iconProps: {},
  contentStyle: {},
};

export default Tooltip;
